import { useEffect, useMemo, useState } from "react";

// Client-side pagination for already-fetched lists. The data is loaded in
// full (as before); this just windows it so long grids don't render hundreds
// of rows at once. `resetKey` snaps back to page 0 whenever the underlying
// filter changes (window / subdomain / hide-forwards), and the page is
// clamped so a shrinking list never strands you on an empty page.
export function usePagination<T>(rows: T[], pageSize: number, resetKey: unknown) {
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [resetKey]);

  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(page, pageCount - 1);
  const start = clamped * pageSize;
  const pageRows = useMemo(() => rows.slice(start, start + pageSize), [rows, start, pageSize]);

  return {
    pageRows,
    page: clamped,
    setPage,
    pageCount,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + pageSize, total),
  };
}

// Presentational pager bar — render below a table. Hidden entirely when the
// list fits on one page.
export function Pager({
  page,
  pageCount,
  total,
  from,
  to,
  onPage,
  unit = "rows",
}: {
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
  onPage: (p: number) => void;
  unit?: string;
}) {
  if (pageCount <= 1) return null;
  const btn =
    "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:hover:bg-transparent";
  return (
    <div className="flex items-center justify-between gap-3 border-t border-slate-200 dark:border-slate-800 px-4 py-2 text-xs text-slate-500 dark:text-slate-400">
      <span className="tabular-nums">
        Showing {from}–{to} of {total} {unit}
      </span>
      <div className="flex items-center gap-2">
        <button type="button" className={btn} disabled={page <= 0} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        <span className="tabular-nums">
          Page {page + 1} / {pageCount}
        </span>
        <button
          type="button"
          className={btn}
          disabled={page >= pageCount - 1}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
